import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";

import { DataTable } from "@/components/DataTable";
import { formatLocation } from "@/data/locations";
import { IWorkOrder, IWorkOrderRevision } from "@/data/work-orders";
import { useFormats } from "@/hooks/Formats";
import { WorkOrderStatusBadge } from "@/views/components/WorkOrder";

export interface IAssignmentWorkOrdersTableProps {
  workOrders: IWorkOrder[];
  onRowClick?: (workOrder: IWorkOrder) => void;
}

export const AssignmentWorkOrdersTable: FC<IAssignmentWorkOrdersTableProps> = memo(({ workOrders, onRowClick }) => {
  const { t } = useTranslation("session");
  const { formatDate, formatRate, formatCost } = useFormats();

  return (
    <DataTable<IWorkOrderRevision>
      data={workOrders}
      uniqueKeyAccessor="id"
      columns={[
        {
          header: t("ASSIGNMENTS.DETAILS.WORK_ORDERS.REVISION"),
          selector: (data: IWorkOrderRevision) => data.version + 1,
          flexGrow: 1,
          maxWidth: "80px",
        },
        {
          header: t("ASSIGNMENTS.DETAILS.WORK_ORDERS.LOCATION"),
          selector: (data: IWorkOrderRevision) => formatLocation(data.location),
          flexGrow: 1,
        },
        {
          header: t("ASSIGNMENTS.DETAILS.WORK_ORDERS.START_DATE"),
          selector: (data: IWorkOrderRevision) => formatDate(data.startDate),
          flexGrow: 1,
          maxWidth: "100px",
        },
        {
          header: t("ASSIGNMENTS.DETAILS.WORK_ORDERS.END_DATE"),
          selector: (data: IWorkOrderRevision) => formatDate(data.actualEndDate ?? data.endDate),
          flexGrow: 1,
          maxWidth: "100px",
        },
        {
          header: t("ASSIGNMENTS.DETAILS.WORK_ORDERS.BUDGET"),
          selector: (data: IWorkOrderRevision) => formatCost(data.estimatedCost, data.rateCurrency),
          flexGrow: 1,
          maxWidth: "110px",
        },
        {
          header: t("ASSIGNMENTS.DETAILS.WORK_ORDERS.RATE"),
          selector: (data: IWorkOrderRevision) => formatRate(data.hourlyRate, data.rateCurrency),
          flexGrow: 1,
          maxWidth: "100px",
        },
        {
          header: t("ASSIGNMENTS.DETAILS.WORK_ORDERS.STATUS"),
          selector: (data: IWorkOrderRevision) => <WorkOrderStatusBadge status={data.status} />,
          flexGrow: 1,
          maxWidth: "200px",
        },
      ]}
      onRowClick={onRowClick}
    />
  );
});
